import { useState, useEffect, useMemo } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { X, Loader2 } from 'lucide-react';
import { api, mediaUrl } from '@/admin/api';

const GalleryPage = () => {
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/gallery').then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    items.forEach((i) => i.category && set.add(i.category));
    return ['all', ...Array.from(set)];
  }, [items]);

  const filtered = tab === 'all' ? items : items.filter((i) => i.category === tab);

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && setSelected(null);
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">Photo Gallery</h1>
          <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
            Memorable moments from our events, cultural celebrations, and community activities.
          </p>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">Gallery is empty.</div>
          ) : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="flex flex-wrap w-full max-w-3xl mx-auto mb-12">
                {categories.map((c) => (
                  <TabsTrigger key={c} value={c} className="capitalize flex-1" data-testid={`gallery-tab-${c}`}>
                    {c}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value={tab}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filtered.map((image) => (
                    <Card
                      key={image.id}
                      className="overflow-hidden cursor-pointer group hover:shadow-elevated transition-elegant"
                      onClick={() => setSelected(image)}
                      data-testid={`gallery-${image.id}`}
                    >
                      <div className="aspect-square overflow-hidden">
                        <img src={mediaUrl(image.image_url)} alt={image.caption || ''} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      </div>
                      {image.caption && (
                        <CardContent className="pt-4 pb-4">
                          <h3 className="font-semibold text-foreground text-sm line-clamp-1">{image.caption}</h3>
                          {image.category && <p className="text-xs text-muted-foreground mt-1 capitalize">{image.category}</p>}
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </section>

      {selected && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <button onClick={() => setSelected(null)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="lightbox-close">
            <X size={24} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={mediaUrl(selected.image_url)} alt={selected.caption || ''} className="w-full h-auto rounded-lg shadow-2xl" />
            {selected.caption && (
              <div className="mt-6 text-center text-white">
                <h3 className="text-2xl font-serif font-bold">{selected.caption}</h3>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
